(ns ui.view.transaction-process.modals.transition.model
  "State + pure helpers for the stage-transition confirmation modal. Its state
  lives under the TP state-path at `[:ui :transition]` (a sibling of the form
  modals; the two are never open at once but kept distinct for clarity)."
  (:require
   [common.transaction-process.notifications :as tp-notifications]
   [common.util :as cu]
   [ui.view.transaction-process.util :as tp-util]))

(def state-key-path [:ui :transition])

(defn state-full-path
  [state-path]
  (into state-path state-key-path))

(defn key-full-path
  [state-path k]
  (conj (state-full-path state-path) k))

(defn backward-transition?
  [transition]
  (= :backward (:transition/direction transition)))

(def ^:private forward-only-transition-keys
  [:transition/action
   :transition/close-action?
   :transition/close-confirmation
   :transition/close-label
   :transition/confirmation
   :transition/fields
   :transition/field-validations
   :transition/forward-label
   :transition/reject-action?
   :transition/required-fields
   :transition/skip-slot-controls
   :transition/to])

(defn as-backward-transition
  "Build the backward action from the current stage's forward transition without
   carrying forward-only terminal actions, fields, or validations into it."
  [transition]
  (let [target (:transition/backward-target transition)]
    (-> (apply dissoc transition forward-only-transition-keys)
        (assoc :transition/to target
               :transition/direction :backward))))

(defn field-visible-in-transition?
  "Apply the same conditional-visibility rules used inside the form so that a
  transition modal hides fields that aren't applicable to the current TP (e.g.
  Lux date for a non-Lux deal). `field-visible?` already treats an absent
  `:field/visibility` as always-visible."
  [field-config value-lookup]
  (tp-util/field-visible? {:field-config field-config
                           :value-lookup value-lookup}))

(defn visible-field-kws
  "The transition's fields filtered to those visible for the current value
  lookup (process merged with vehicle rule keys)."
  [{:keys [transition template value-lookup]}]
  (if (backward-transition? transition)
    []
    (->> (:transition/fields transition)
         (filter (fn [kw]
                   (field-visible-in-transition?
                    (get-in template [:template/fields kw])
                    value-lookup)))
         vec)))

(defn mandatory-field-kws
  "Of the given visible transition field kws, the subset that resolve to
  mandatory for the current value-lookup. Lets transition-modal fields honour
  the same `:field/mandatoriness` config as on-card fields (e.g. the anticipated
  decision date is mandatory for certain transaction types)."
  [{:keys [visible-field-kws template value-lookup]}]
  (->> visible-field-kws
       (filterv (fn [kw]
                  (let [fc (get-in template [:template/fields kw])]
                    (tp-util/field-mandatory? fc value-lookup))))))

(defn init-field-vals
  "Seed the editable transition field values from the process. Currency fields
  also seed their symbol (defaulting to the tenant currency)."
  [{:keys [visible-field-kws field-configs entity-data default-currency]}]
  (->> visible-field-kws
       (mapcat (fn [kw]
                 (if (= :data.type/currency-value (get-in field-configs [kw :field/data-type]))
                   (let [sym-kw (cu/currency->symbol kw)]
                     [[kw (get entity-data kw)]
                      [sym-kw (or (get entity-data sym-kw) default-currency)]])
                   [[kw (get entity-data kw)]])))
       (into {})))

(defn skip-slot-controls
  "Resolve template-configured skip controls to the persisted requiredness field
   and its explicit Not needed value. Invalid slot references are omitted rather
   than producing a checkbox that cannot persist its state."
  [{:keys [transition template]}]
  (if (backward-transition? transition)
    []
    (->> (:transition/skip-slot-controls transition)
         (keep (fn [{:keys [slot-kw label-key]}]
                 (let [required-config (get-in template
                                               [:template/dictionary slot-kw :slot/required])
                       needed-field (:from-field required-config)
                       not-needed-value (:false-value required-config)]
                   (when (and needed-field not-needed-value)
                     {:slot-kw          slot-kw
                      :label-key        label-key
                      :needed-field     needed-field
                      :not-needed-value not-needed-value}))))
         vec)))

(defn init-skip-slot-vals
  [controls entity-data]
  (->> controls
       (map (fn [{:keys [slot-kw needed-field not-needed-value]}]
              [slot-kw (= not-needed-value (get entity-data needed-field))]))
       (into {})))

(defn skip-slot-field-vals
  "Translate inverse checkbox state to persisted requiredness. Unchecked is nil
   so it retracts any prior value and remains identical to never being set."
  [controls skip-slot-vals]
  (->> controls
       (map (fn [{:keys [slot-kw needed-field not-needed-value]}]
              [needed-field (when (get skip-slot-vals slot-kw)
                              not-needed-value)]))
       (into {})))

(defn resolve-notification-recipients
  "Deduped list of recipients shaped `{:value account-id :label \"Name (Role)\"}`.
  The team role label comes from `:template/team-roles` so the preview matches
  what users see elsewhere."
  [{:keys [template transition current-stage team-assignments]}]
  (let [event            {:notification-type :stage-transition
                          :from-stage        current-stage
                          :to-stage          (:transition/to transition)
                          :direction         (or (:transition/direction transition)
                                                 :forward)}
        rules            (tp-notifications/match-rules (:template/notifications template) event)
        teams            (tp-notifications/resolve-recipient-teams rules)
        role-label-by-id (->> (:template/team-roles template)
                              (map (fn [{:team-role/keys [id label]}] [id label]))
                              (into {}))]
    (->> teams
         (mapcat (fn [team-id]
                   (let [role-label (get role-label-by-id team-id)]
                     (map (fn [m]
                            (let [base (if (map? m) m {:value m :label (str m)})]
                              (cond-> base
                                role-label
                                (update :label (fn [lbl] (str lbl " (" role-label ")"))))))
                          (get team-assignments team-id)))))
         (cu/distinct-by :value)
         vec)))
